import { ViewAction } from "store/actions";
import { DocumentQuestion } from "store/types";

import { IAction, IStore } from "../..";
import { IDealModule, IView, IWindowSize, LeftSliderType, ShareDialog } from "../../types";
import { reducer } from "../index";

type ShareTypeEntityKeyCollection = Record<
  ShareDialog["type"],
  keyof (Pick<IDealModule, "forms" | "notes"> & Pick<IStore, "documents">)
>;

const shareTypeEntityKeyCollection: Partial<ShareTypeEntityKeyCollection> = {
  form_id: "forms",
  note_id: "notes",
  document_id: "documents"
};

reducer.createHandler(ViewAction.CommentsScroll, (state, { payload }: IAction<boolean>) => ({
  ...state,
  view: {
    ...state.view,
    commentsScroll: payload
  }
}));

reducer.createHandler(ViewAction.ScrollToAnswer, (state, { payload }: IAction<"on" | "off">) => ({
  ...state,
  view: {
    ...state.view,
    notificationScroll: {
      ...state.view.notificationScroll,
      scrollToAnswer: payload
    }
  }
}));

reducer.createHandler(ViewAction.SetShareDialog, (state, { payload }: IAction<ShareDialog | null>) => {
  const injectHolderId = (payload: ShareDialog | null) => {
    if (!payload) return payload;
    if (!payload.type) return payload;
    if (payload.type === "deal_id") return payload;

    const elementId = payload.elements[0];
    if (!elementId) return payload;

    // Handle folders
    if (payload.type === "section_id") {
      const sharableItem = state.dealModule.folders[elementId];
      if (!sharableItem) return payload;

      return {
        ...payload,
        holderId: sharableItem.parent_id
      };
    }

    // Handle files
    if (payload.type === "file_id") {
      const sharableItem = state.dealModule.files[elementId];
      if (!sharableItem) return payload;

      return {
        ...payload,
        holderId: sharableItem.form_id || sharableItem.section_id
      };
    }

    // Handle rest ones
    const entityType = shareTypeEntityKeyCollection[payload.type];
    if (!entityType) return payload;

    const sharableElement =
      entityType === "documents" ? state[entityType]?.[elementId] : state.dealModule[entityType]?.[elementId];

    if (!sharableElement) return payload;

    return {
      ...payload,
      holderId: sharableElement.section_id
    };
  };

  return {
    ...state,
    view: {
      ...state.view,
      shareDialog: injectHolderId(payload)
    }
  };
});

reducer.createHandler(ViewAction.SetWindowSize, (state, { payload }: IAction<IWindowSize>) => ({
  ...state,
  view: {
    ...state.view,
    windowSize: payload
  }
}));

reducer.createHandler(ViewAction.SetLeftSlider, (state, { payload }: IAction<LeftSliderType>) => ({
  ...state,
  view: {
    ...state.view,
    leftSlider: payload
  }
}));

reducer.createHandler(ViewAction.ScrollToQuestion, (state, { payload }: IAction<boolean>) => ({
  ...state,
  view: {
    ...state.view,
    scrollToQuestion: payload
  }
}));

reducer.createHandler(ViewAction.EmailBoxUpdate, (state, { payload }: IAction<boolean>) => ({
  ...state,
  view: {
    ...state.view,
    emailBoxUpdate: payload
  }
}));

reducer.createHandler(ViewAction.SetError409, (state, { payload }: IAction<string>) => ({
  ...state,
  view: {
    ...state.view,
    error409: payload
  }
}));

reducer.createHandler(ViewAction.SetDealRedirect, (state, { payload }: IAction<IView["dealRedirect"]>) => ({
  ...state,
  view: {
    ...state.view,
    dealRedirect: payload
  }
}));

reducer.createHandler(ViewAction.SetCrucnbaseDrawerState, (state, { payload }: IAction<boolean>) => ({
  ...state,
  view: {
    ...state.view,
    cruchbaseDrawerState: payload
  }
}));

reducer.createHandler(ViewAction.SetCryptoCurrencyDrawerState, (state, { payload }: IAction<boolean>) => ({
  ...state,
  view: {
    ...state.view,
    cryptoCurrencyDrawerState: payload
  }
}));

reducer.createHandler(
  ViewAction.SetCreateManyMeasureData,
  (state, { payload }: IAction<IView["createManyMeasureData"]>) => ({
    ...state,
    view: {
      ...state.view,
      createManyMeasureData: payload
    }
  })
);

reducer.createHandler(ViewAction.SetSelectedQuestion, (state, { payload }: IAction<DocumentQuestion | null>) => ({
  ...state,
  view: {
    ...state.view,
    selectedQuestion: payload
  }
}));

reducer.createHandler(ViewAction.SetQuickViewAddNewLine, (state, { payload }: IAction<boolean>) => ({
  ...state,
  view: {
    ...state.view,
    quickViewAddNewLine: payload
  }
}));

reducer.createHandler(ViewAction.SetIsDealCharBarOpen, (state, { payload }: IAction<boolean>) => ({
  ...state,
  view: {
    ...state.view,
    isDealCharBarOpen: payload
  }
}));

reducer.createHandler(ViewAction.SetSidebarState, (state, { payload }: IAction<boolean>) => ({
  ...state,
  view: {
    ...state.view,
    sidebarState: payload
  }
}));
